import React from 'react';
import './typography.css';

/**
 * Helper function to get weight and color classes
 */
const getTextClasses = (weight = 'normal', emphasis = false) => {
    const weightClasses = {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold'
    };

    const colorClass = emphasis ? 'text-paragraph-emphasis' : 'text-paragraph';

    return {
        weightClass: weightClasses[weight],
        colorClass
    };
};

/**
 * Heading Components
 */
export const Heading1 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h1 className={`text-h1 font-extrabold ${colorClass} ${className}`} {...props}>
            {children}
        </h1>
    );
};

export const Heading2 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h2 className={`text-h2 font-bold ${colorClass} ${className}`} {...props}>
            {children}
        </h2>
    );
};

export const Heading3 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h3 className={`text-h3 font-bold ${colorClass} ${className}`} {...props}>
            {children}
        </h3>
    );
};

export const Heading4 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h4 className={`text-h4 font-bold ${colorClass} ${className}`} {...props}>
            {children}
        </h4>
    );
};

export const Heading5 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h5 className={`text-h5 font-bold ${colorClass} ${className}`} {...props}>
            {children}
        </h5>
    );
};

export const Heading6 = ({ children, emphasis = true, className = '', ...props }) => {
    const { colorClass } = getTextClasses('normal', emphasis);
    return (
        <h6 className={`text-h6 font-bold ${colorClass} ${className}`} {...props}>
            {children}
        </h6>
    );
};

/**
 * LeadingParagraph — display/marketing copy only (24px). Not for app body text.
 */
export const LeadingParagraph = ({
    children,
    weight = 'normal',
    emphasis = false,
    className = '',
    ...props
}) => {
    const { weightClass, colorClass } = getTextClasses(weight, emphasis);

    return (
        <p className={`text-body-xl ${weightClass} ${colorClass} ${className}`} {...props}>
            {children}
        </p>
    );
};

export const NormalParagraph = ({
    children,
    weight = 'normal',
    emphasis = false,
    className = '',
    ...props
}) => {
    const { weightClass, colorClass } = getTextClasses(weight, emphasis);

    return (
        <p className={`text-body-lg ${weightClass} ${colorClass} ${className}`} {...props}>
            {children}
        </p>
    );
};

/**
 * Label Text — form labels and inline section titles (16px).
 * Do not use for body paragraphs; use BodyText instead.
 */
export const LabelText = ({
    children,
    weight = 'semibold',
    emphasis = true,
    className = '',
    ...props
}) => {
    const { weightClass, colorClass } = getTextClasses(weight, emphasis);

    return (
        <p className={`text-label ${weightClass} ${colorClass} ${className}`} {...props}>
            {children}
        </p>
    );
};

/**
 * Body Text — standard body copy for the whole app (12px / 150%).
 * Prefer this (or XsmallText / SmallText aliases) for paragraphs, values,
 * captions, and any non-heading text. Do not invent ad-hoc text sizes.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Text content
 * @param {string} props.weight - Font weight ('normal', 'medium', 'semibold', 'bold')
 * @param {boolean} props.emphasis - Whether to use emphasis color (default: false)
 * @param {string} props.className - Additional CSS classes
 */
export const BodyText = ({
    children,
    weight = 'normal',
    emphasis = false,
    className = '',
    ...props
}) => {
    const { weightClass, colorClass } = getTextClasses(weight, emphasis);

    return (
        <p className={`text-body ${weightClass} ${colorClass} ${className}`} {...props}>
            {children}
        </p>
    );
};

/** @deprecated Prefer BodyText — same size (text-body). Kept for existing call sites. */
export const SmallText = BodyText;

/** @deprecated Prefer BodyText — same size (text-body). Kept for existing call sites. */
export const XsmallText = BodyText;

/**
 * Quote Component
 */
export const Quote = ({ children, className = '', ...props }) => (
    <blockquote className={`text-blockquote font-bold italic text-quote ${className}`} {...props}>
        {children}
    </blockquote>
);
